import { supabase } from '../config/supabase';

export interface MediaWisdomQuestion {
  id: string;
  question: string;
  choice_a: string;
  choice_b: string;
  choice_c: string;
  choice_d: string;
  correct_answer: 'A' | 'B' | 'C' | 'D';
  explanation: string;
  label: string;
  order_index: number;
}

export interface MediaWisdomAttempt {
  id: string;
  user_id: string;
  started_at: string;
  completed_at: string | null;
  score: number | null;
  answers: Record<string, string>;
  results_by_label: Record<string, { correct: number; total: number }>;
}

export interface QuestionWithUserAnswer extends MediaWisdomQuestion {
  userAnswer?: string;
  isCorrect?: boolean;
}

class MediaWisdomAssessmentApi {
  /**
   * Get all media wisdom assessment questions
   */
  async getQuestions(): Promise<MediaWisdomQuestion[]> {
    const { data, error } = await supabase
      .from('media_wisdom_assessment_questions')
      .select('*')
      .order('order_index', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  /**
   * Create a new assessment attempt
   */
  async createAttempt(userId: string): Promise<MediaWisdomAttempt> {
    const { data, error } = await supabase
      .from('media_wisdom_assessment_attempts')
      .insert({
        user_id: userId,
        started_at: new Date().toISOString(),
        answers: {},
        results_by_label: {}
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Submit completed assessment
   */
  async submitAttempt(
    attemptId: string,
    answers: Record<string, string>,
    questions: MediaWisdomQuestion[]
  ): Promise<MediaWisdomAttempt> {
    // Calculate score and results by label
    let totalCorrect = 0;
    const resultsByLabel: Record<string, { correct: number; total: number }> = {};

    questions.forEach((question) => {
      const userAnswer = answers[question.id];
      const isCorrect = userAnswer === question.correct_answer;

      if (isCorrect) totalCorrect++;

      // Track by label
      if (!resultsByLabel[question.label]) {
        resultsByLabel[question.label] = { correct: 0, total: 0 };
      }
      resultsByLabel[question.label].total++;
      if (isCorrect) {
        resultsByLabel[question.label].correct++;
      }
    });

    const { data, error } = await supabase
      .from('media_wisdom_assessment_attempts')
      .update({
        completed_at: new Date().toISOString(),
        score: totalCorrect,
        answers,
        results_by_label: resultsByLabel
      })
      .eq('id', attemptId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Get user's assessment history
   */
  async getUserAttempts(userId: string): Promise<MediaWisdomAttempt[]> {
    const { data, error } = await supabase
      .from('media_wisdom_assessment_attempts')
      .select('*')
      .eq('user_id', userId)
      .order('started_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  /**
   * Get a specific attempt with questions
   */
  async getAttemptWithQuestions(attemptId: string): Promise<{
    attempt: MediaWisdomAttempt;
    questions: QuestionWithUserAnswer[];
  }> {
    // Get attempt
    const { data: attempt, error: attemptError } = await supabase
      .from('media_wisdom_assessment_attempts')
      .select('*')
      .eq('id', attemptId)
      .single();

    if (attemptError) throw attemptError;

    // Get all questions
    const questions = await this.getQuestions();

    // Merge with user answers
    const questionsWithAnswers: QuestionWithUserAnswer[] = questions.map((q) => ({
      ...q,
      userAnswer: attempt.answers[q.id],
      isCorrect: attempt.answers[q.id] === q.correct_answer
    }));

    return {
      attempt,
      questions: questionsWithAnswers
    };
  }
}

export const mediaWisdomAssessmentApi = new MediaWisdomAssessmentApi();

